/**
 * `/registration` — Public 1-page explainer for the $25 dog registration fee.
 *
 * The /pricing page exists for full plan comparison; this page exists
 * because cold visitors and persona-targeted ads (FB, IG, Reddit) need
 * a one-sentence answer to "what does $25 actually get me?". One CTA,
 * no upsells. Linked from the landing page hero, the dog-registration
 * onboarding step, and the persona one-pager PDFs.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import LeadMagnetBlock from "@/components/LeadMagnetBlock";
import FoundingPromoBanner from "@/components/FoundingPromoBanner";
import { isNativeApp } from "@/lib/nativeBridge";
import NativePlanNotice from "@/components/NativePlanNotice";
import { CheckCircleIcon as CheckCircle, CertificateIcon as Certificate, TreeStructureIcon as TreeStructure, ShieldCheckIcon as ShieldCheck, GraduationCapIcon as GraduationCap, PawPrintIcon as PawPrint, SparkleIcon as Sparkle, ArrowRightIcon as ArrowRight, StorefrontIcon as Storefront, TrophyIcon as Trophy } from "@phosphor-icons/react";

const INCLUDED = [
  {
    icon: Certificate,
    title: "Permanent registration certificate",
    body: "A digital, signed certificate with your dog's unique registration number — yours to keep forever, even if you cancel. Printable, shareable, and built on a tamper-evident registry.",
  },
  {
    icon: TreeStructure,
    title: "Pedigree built and stored for life",
    body: "Sire, dam, and ancestors linked into a live pedigree tree. Add depth as you go; descendants automatically inherit the lineage. No re-payment to extend the tree later.",
  },
  {
    icon: ShieldCheck,
    title: "Eligibility for DNA-verified status",
    body: "Once you submit a DNA panel (Embark, Wisdom, or any accredited lab), your dog earns a visible DNA-verified badge that surfaces in every search and on every public profile.",
  },
  {
    icon: PawPrint,
    title: "Public dog profile + shareable link",
    body: "An SEO-indexed profile page at /d/<id> with photos, titles, achievements, health clearances, and offspring. Share it on social, email it to buyers, or embed it on your own site.",
  },
  {
    icon: Storefront,
    title: "Marketplace eligibility",
    body: "Registered dogs (and their puppies) can be listed in the Armstrong K9 Marketplace — the rest of the listing is free. Unregistered dogs cannot be sold through the platform.",
  },
  {
    icon: GraduationCap,
    title: "Eligibility for events, titles, and breed catalog",
    body: "Enter Armstrong-hosted events, claim earned titles on your dog's profile, and appear in the breed catalog rotation. Registration is the gate; participation is free.",
  },
  {
    icon: Trophy,
    title: "Offspring of registered parents stay registered",
    body: "Any future litter from this dog has automatic eligibility for litter registration ($75/litter, includes every puppy in the litter). The lineage chain never breaks.",
  },
];

const NOT_INCLUDED = [
  "Care+ subscription (meal/walk/health tracking — sold separately)",
  "Buyer Pass (unlimited inquiries + pedigree-depth unlocks — sold separately)",
  "Kennel Elite / Pro breeder plans (multi-dog management + marketing — sold separately)",
  "Storefront merchandise sales (separate $19/mo subscription)",
  "Service-provider directory listings (free for approved trainers, groomers, walkers, sitters)",
];

export default function RegistrationPricing() {
  // App Store 3.1.1 — no pricing/purchase UI inside the native shell.
  if (isNativeApp()) return <NativePlanNotice />;
  return (
    <div className="min-h-screen bg-[#FAFAFA] font-manrope text-ink">
      <Helmet>
        <title>{"$25 Dog Registration — what's included · Armstrong K9 Registry"}</title>
        <meta name="description" content="Register your dog with Armstrong K9 Registry for $25. Includes permanent certificate, pedigree, DNA-verified eligibility, shareable public profile, and marketplace eligibility — for the lifetime of your dog." />
      </Helmet>
      <FoundingPromoBanner />

      <header className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="reg-brand">
            <img src="/logo-256.png" alt="" className="h-8 w-8 rounded-full object-contain" />
            <span className="font-spectral text-base tracking-tight">Armstrong K9 Registry</span>
          </Link>
          <nav className="hidden sm:flex items-center gap-5 text-sm text-[#3F3F3F]">
            <Link to="/pricing" className="hover:text-ink">All pricing</Link>
            <Link to="/login" className="hover:text-ink">Sign in</Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-10">
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-bold">Dog registration</p>
        <h1 className="font-spectral text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3 leading-[1.05]">
          $25. One time. Per dog. For life.
        </h1>
        <p className="text-base sm:text-lg text-[#3F3F3F] mt-5 max-w-2xl leading-relaxed">
          A flat fee that registers your dog into the Armstrong K9 Registry permanently &mdash;
          no annual renewal, no &ldquo;membership&rdquo; lock-in, no expiring certificate. Pay once,
          own the record for the lifetime of your dog (and beyond, for pedigree purposes).
        </p>
        <div className="mt-7 flex flex-wrap items-center gap-3">
          <Link
            to="/register"
            className="inline-flex items-center gap-2 rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white px-6 py-2.5 text-sm font-semibold tracking-wide transition-colors"
            data-testid="reg-cta-primary"
          >
            Create your account <ArrowRight size={14} />
          </Link>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-1.5 text-sm text-[#666] hover:text-ink"
            data-testid="reg-cta-secondary"
          >
            Compare all plans →
          </Link>
        </div>
      </section>

      {/* Lead-magnet email capture — sits between the hero and the
          "What $25 actually unlocks" section. Cold ad traffic that
          isn't ready to pay the $25 yet still gives us their email
          for the 7-day drip sequence. */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 pb-2">
        <LeadMagnetBlock source="registration_page" />
      </section>

      {/* What you get */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <h2 className="font-spectral text-3xl tracking-tight">What $25 actually unlocks</h2>
        <p className="text-sm text-[#666] mt-1.5">Every item below is included in the one-time fee. No upsells inside.</p>
        <ul className="grid sm:grid-cols-2 gap-4 mt-8" data-testid="reg-included">
          {INCLUDED.map((item) => {
            const I = item.icon;
            return (
              <li key={item.title} className="bg-white border border-[#EAEAEA] rounded-2xl p-5 flex gap-4" data-testid={`reg-included-${item.title.toLowerCase().split(" ")[0]}`}>
                <div className="h-10 w-10 shrink-0 rounded-full bg-[#0E0E0E] grid place-items-center">
                  <I size={18} weight="duotone" className="text-gold" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-[15px]">{item.title}</p>
                  <p className="text-[13.5px] text-[#666] mt-1 leading-relaxed">{item.body}</p>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {/* What it doesn't include */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-6">
        <div className="bg-white border border-[#EAEAEA] rounded-2xl p-6">
          <h2 className="font-spectral text-2xl tracking-tight">What $25 does NOT include</h2>
          <p className="text-sm text-[#666] mt-1.5">
            We sell registration and subscriptions separately on purpose. Registration is a
            one-time, permanent record. Subscriptions are recurring tools.
          </p>
          <ul className="mt-4 space-y-2" data-testid="reg-not-included">
            {NOT_INCLUDED.map((line) => (
              <li key={line} className="flex items-start gap-2 text-[13.5px] text-[#3F3F3F]">
                <span className="text-[#999] mt-0.5">·</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <p className="text-[12px] text-[#888] mt-5">
            Want the full plan grid? See <Link to="/pricing" className="underline">/pricing</Link>.
          </p>
        </div>
      </section>

      {/* Trust strip */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <div className="grid sm:grid-cols-3 gap-4">
          <TrustStat eyebrow="No renewal" body="Pay once. Your certificate never expires." />
          <TrustStat eyebrow="14-day refund" body="Full refund within 14 days if you change your mind." />
          <TrustStat eyebrow="Yours forever" body="Cancel any subscription &mdash; your registered dogs stay registered." />
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-14 text-center">
        <Sparkle size={28} className="mx-auto text-gold" weight="duotone" />
        <h2 className="font-spectral text-3xl sm:text-4xl tracking-tight mt-3">
          Ready to register your dog?
        </h2>
        <p className="text-sm text-[#666] mt-3 max-w-xl mx-auto">
          Create your free account, then pay the $25 registration fee when you're ready to issue the
          certificate. No surprise charges.
        </p>
        <div className="mt-6 flex justify-center gap-3 flex-wrap">
          <Link
            to="/register"
            className="inline-flex items-center gap-2 rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white px-6 py-2.5 text-sm font-semibold tracking-wide transition-colors"
            data-testid="reg-cta-final"
          >
            Create your account <ArrowRight size={14} />
          </Link>
          <Link
            to="/dna-verified"
            className="inline-flex items-center gap-1.5 text-sm text-[#666] hover:text-ink rounded-full border border-[#E5E5E5] px-5 py-2.5"
            data-testid="reg-learn-dna"
          >
            Learn about DNA verification
          </Link>
        </div>
      </section>

      <footer className="border-t border-[#E5E5E5] bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-8 text-xs text-[#888] flex flex-wrap items-center justify-between gap-3">
          <span>© Armstrong K9 Registry</span>
          <div className="flex items-center gap-4">
            <Link to="/refund" className="hover:text-ink">Refund policy</Link>
            <Link to="/terms" className="hover:text-ink">Terms</Link>
            <Link to="/privacy" className="hover:text-ink">Privacy</Link>
            <Link to="/contact" className="hover:text-ink">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function TrustStat({ eyebrow, body }) {
  return (
    <div className="bg-white border border-[#EAEAEA] rounded-2xl p-5 flex items-start gap-3">
      <CheckCircle size={20} weight="fill" className="text-[#0F8043] shrink-0 mt-0.5" />
      <div>
        <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-gold">{eyebrow}</p>
        <p className="text-sm text-ink mt-1 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
